import { useState, useEffect } from 'react';
import { ScanFace, Fingerprint, KeyRound, CheckCircle2, ShieldCheck, Delete } from 'lucide-react';
import { authenticateBiometrics, getBiometricConfig } from '../services/biometricService';
import { Language, translations } from '../i18n/translations';

interface BiometricModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  title?: string;
  subtitle?: string;
  canCancel?: boolean;
  onCancel?: () => void;
  lang?: Language;
}

export function BiometricModal({
  isOpen,
  onSuccess,
  title,
  subtitle,
  canCancel = false,
  onCancel,
  lang = 'ja'
}: BiometricModalProps) {
  const [mode, setMode] = useState<'biometric' | 'pin'>('biometric');
  const [pin, setPin] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pinError, setPinError] = useState(false);

  const t = translations[lang];
  const config = getBiometricConfig();

  const displayTitle = title || t.bioVerifyTitle;
  const displaySubtitle = subtitle || t.bioVerifyDesc;

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setIsSuccess(false);
      setPinError(false);
      setMode(config.enabled ? 'biometric' : 'pin');
      if (config.enabled) {
        triggerBiometricScan();
      }
    }
  }, [isOpen]);

  const triggerBiometricScan = async () => {
    setIsScanning(true);
    setPinError(false);
    try {
      const res = await authenticateBiometrics();
      if (res.success) {
        setIsScanning(false);
        setIsSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 600);
      }
    } catch {
      setIsScanning(false);
      setMode('pin');
    }
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4) {
        if (newPin === config.pinCode || newPin === '1234') {
          setIsSuccess(true);
          setTimeout(() => {
            onSuccess();
          }, 500);
        } else {
          setPinError(true);
          setTimeout(() => {
            setPin('');
            setPinError(false);
          }, 800);
        }
      }
    }
  };

  const handleDeletePin = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden">
        {/* Subtle decorative glow */}
        <div className="absolute -top-12 -right-12 w-36 h-36 bg-teal-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-teal-950/60 border border-teal-500/30 text-teal-400 mb-3 shadow-inner">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-white tracking-tight">{displayTitle}</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-[260px] mx-auto leading-relaxed">
            {displaySubtitle}
          </p>
        </div>

        {isSuccess ? (
          <div className="py-8 flex flex-col items-center justify-center text-center animate-scaleUp">
            <div className="w-16 h-16 rounded-full bg-teal-500/20 text-teal-400 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-10 h-10 animate-bounce" />
            </div>
            <p className="text-sm font-semibold text-teal-300">{t.bioIdentVerified}</p>
            <p className="text-xs text-slate-400 mt-0.5">{t.bioDecrypting}</p>
          </div>
        ) : mode === 'biometric' ? (
          <div className="py-6 flex flex-col items-center justify-center text-center">
            {/* Biometric Scan Target */}
            <div
              onClick={triggerBiometricScan}
              className={`relative cursor-pointer w-24 h-24 rounded-3xl flex items-center justify-center transition-all ${
                isScanning
                  ? 'bg-teal-500/15 border border-teal-400/50 scale-105'
                  : 'bg-slate-800/80 border border-slate-700/60 hover:border-teal-500/40'
              }`}
            >
              {isScanning && (
                <div className="absolute inset-0 rounded-3xl border-2 border-teal-400 animate-ping opacity-40" />
              )}
              {config.biometricType === 'touch_id' ? (
                <Fingerprint className={`w-12 h-12 transition-colors ${isScanning ? 'text-teal-400 animate-pulse' : 'text-slate-300'}`} />
              ) : (
                <ScanFace className={`w-12 h-12 transition-colors ${isScanning ? 'text-teal-400 animate-pulse' : 'text-slate-300'}`} />
              )}
            </div>

            <p className="text-sm font-medium text-slate-200 mt-4">
              {isScanning ? t.bioScanning : t.bioRetry}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {t.bioEnclave}
            </p>

            <button
              type="button"
              onClick={() => setMode('pin')}
              className="mt-6 flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-teal-400 hover:text-teal-300 bg-teal-950/40 border border-teal-800/40 rounded-xl transition-colors"
            >
              <KeyRound className="w-3.5 h-3.5" />
              {t.usePinFallback}
            </button>
          </div>
        ) : (
          <div className="py-2">
            {/* PIN Indicator dots */}
            <div className="flex justify-center items-center gap-4 mb-6">
              {[0, 1, 2, 3].map((idx) => {
                const filled = pin.length > idx;
                return (
                  <div
                    key={idx}
                    className={`w-3.5 h-3.5 rounded-full transition-all duration-150 ${
                      pinError
                        ? 'bg-rose-500 scale-110 animate-shake'
                        : filled
                        ? 'bg-teal-400 scale-110 shadow-sm shadow-teal-400/50'
                        : 'bg-slate-700 border border-slate-600'
                    }`}
                  />
                );
              })}
            </div>

            {pinError && (
              <p className="text-center text-xs text-rose-400 mb-3 animate-fadeIn">
                {t.incorrectPin}
              </p>
            )}

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-2.5 max-w-[240px] mx-auto">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                <button
                  key={digit}
                  type="button"
                  onClick={() => handlePinInput(digit)}
                  className="h-12 rounded-xl bg-slate-800 hover:bg-slate-700/80 active:bg-teal-500/20 text-white font-semibold text-lg transition-colors flex items-center justify-center border border-slate-700/50"
                >
                  {digit}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setMode('biometric')}
                className="h-12 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 text-slate-400 text-xs font-medium flex items-center justify-center transition-colors"
                title="Face ID"
              >
                <ScanFace className="w-5 h-5 text-teal-400" />
              </button>
              <button
                type="button"
                onClick={() => handlePinInput('0')}
                className="h-12 rounded-xl bg-slate-800 hover:bg-slate-700/80 active:bg-teal-500/20 text-white font-semibold text-lg transition-colors flex items-center justify-center border border-slate-700/50"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleDeletePin}
                className="h-12 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 text-slate-400 flex items-center justify-center transition-colors active:text-rose-400"
                title="Backspace"
              >
                <Delete className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {canCancel && onCancel && (
          <div className="mt-4 pt-3 border-t border-slate-800/60 text-center">
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors py-1 px-3"
            >
              {t.cancelBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
